import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { RaciNavigationService } from '../../../../navigation/raci-navigation.service';
import { ActionStageEnum } from '../../../raci/models/enums/action-stage-enum';
import { RaciService } from '../../../raci/services/raci.service';
import { TaskTypeEnum } from '../../models/task-type-enum';
import { environment } from '../../../../../environments/environment';
import { EventBusService } from '../../../../services/event-bus.service';
import { AppStorageService } from '../../../../services/app-storage.service';

/**
 * Handles the user tasks pane component
 */
@Component({
  selector: 'user-tasks-pane',
  templateUrl: './user-tasks-pane.component.html',
  styleUrls: ['./user-tasks-pane.component.css']
})
export class UserTasksPaneComponent implements OnDestroy {
  /**
  * Sets the tasks data and updates the corresponding properties.
  * @param value The tasks data.
  */
  @Input() set tasks(value: any) {
    if (value) {
      this.approvalTasks = value.approvalTasks;
      this.valoremTasks = value.valoremTasks;
      this.argusTasks = value.argusTasks;
      this.valuationEventTasks = value.valuationEventTasks;
      this.generalUserAssignmentsData = value.generalTasks;
      this.mappingGeneralUserAssignmentsData();
      this.currentUser = this.appStorage.getCurrentUserDetails();
      this.userTasks = value;
      this.filterData(this.userTasks);
      this.updateTasksStatus(this.currentUser);
    }
  }

  /**
   * Sets the refresh status and updates the tasks status.
   * @param value The refresh status.
   */
  @Input() set refresh(value: boolean) {
    this.updateTasksStatus(this.currentUser);
  }

  /**
   * Emits the approval matrix of a task.
   */
  @Output() approvalMatrixOfTask = new EventEmitter<any>();

  /**
   * Emits the change of user's active task.
   */
  @Output() onChangeUserActiveTask = new EventEmitter<any>();

  /**
   * Emits the approval matrix of a general task.
   */
  @Output() generalTaskApprovalMatrix = new EventEmitter<any>();

  /**
   * Emits the approval matrix of a function task.
   */
  @Output() approvalMatrixOfFunctionTask = new EventEmitter<any>();

  /**
   * Emits the completion of a task.
   */
  @Output() onCompleteTask = new EventEmitter<any>();

  /**
   * Emits the set valuation setup task.
   */
  @Output() setValuationSetupTask = new EventEmitter<any>();

  /**
  * Holds the approval tasks data.
  */
  approvalTasks: any;

  /**
   * Holds the user tasks data.
   */
  userTasks: any;

  /**
   * Holds the Valorem tasks data.
   */
  valoremTasks: any;

  /**
   * Holds the Argus tasks data.
   */
  argusTasks: any;

  /**
   * Holds the valuation event tasks data.
   */
  valuationEventTasks: any;

  /**
   * Holds the general user assignments data.
   */
  generalUserAssignmentsData: any;

  /**
   * Holds the active project ID.
   */
  activeProjectId;

  /**
   * Holds the active valuation event ID.
   */
  activeValEventId;

  /**
   * Holds the active task ID.
   */
  activeTaskId;

  /**
   * Holds the current user data.
   */
  currentUser: any;

  /**
   * Specifies whether the user tasks should be refreshed.
   */
  shouldRefreshUserTasks = false;

  /**
   * Specifies whether an active task assignment is in progress.
   */
  activeTaskAssignment = false;

  /**
   * Active general task
   */
  activeGeneralTask: any;

  /**
   * Holds the filtered data for tasks.
   */
  filteredData: Array<{
    "project": any,
    "valuationEvents": Array<{
      "valEvent": any,
      "tasks": Array<{
        "taskInfo": any,
        "properties": Array<any>
      }>
    }>
  }> = [];

  /**
   * Holds the subscriptions for the component.
   */
  private _subscriptions: Array<Subscription> = new Array<Subscription>();

  /**
   * Constructor
   * @param appStorage
   * @param eventBus
   * @param _raciNavigationService
   * @param _raciService
   */
  constructor(private appStorage: AppStorageService,
    private eventBus: EventBusService,
    private _raciNavigationService: RaciNavigationService,
    private _raciService: RaciService) { }

  /**
 * OnDestroy
 */
  ngOnDestroy(): void {
    this._subscriptions.forEach(subscription => { subscription.unsubscribe() });
  }

  /**
   * OnChanges
   */
  ngOnChanges() {
    if (this.shouldRefreshUserTasks) {
      this.shouldRefreshUserTasks = false;
      this.userTasks.approvalTasks = this.userTasks.approvalTasks.filter(item => { return !item.dateApproved1 || !item.dateApproved2 || !item.dateApproved3 })
      this.filterData(this.userTasks);
      this.updateTasksStatus(this.currentUser);
      this.onChangeUserActiveTask.emit(this.activeTaskId);
    }
  }

  /**
   * Filter task where user have approval
   * @param data User taks
   */
  filterData(data: any) {
    this.filteredData = [];
    this.filterValuationEventTasks();
    this.filterApprovalTasks(data);
    this.filterArgusTasks();
    this.filterValoremTasks();
    this.filterGeneralTasks(data);
    this.filteredData.sort((a, b) => { return a.project.projectId > b.project.projectId ? -1 : 1 });
  }

  /**
   * Filter valuation event tasks data
   */
  filterValuationEventTasks() {
    this.valuationEventTasks.forEach(property => {
      //check if filtered data contains project info
      let project = this.filteredData.find(data => { return data.project.projectId == property.projectId });
      //create project if not provided
      if (!project) {
        this.filteredData.push({ "project": this.createNewProject(property), "valuationEvents": [] });
        project = this.filteredData.find(data => { return data.project.projectId == property.projectId })
        this.mappingValuationData(project, property, true);
      } else {
        this.mappingValuationData(project, property);
      }
    });
  }

  /**
   * Mapping general user assignments data
   */
  mappingGeneralUserAssignmentsData() {
    this.generalUserAssignmentsData.forEach(assignment => assignment.isSelected = false);
  }

  /**
   * Check if general assignment has selected general task
   * @param generalAssignment General assignment
   */
  hasSelectedTask(generalAssignment: any) {
    return generalAssignment?.generalTasks?.find(task => task.oid == this.activeGeneralTask?.oid) != null;
  }

  /**
  * Filter approval tasks data
  * @param data Tasks data
  */
  filterApprovalTasks(data: any) {
    data?.approvalTasks?.forEach(property => {
      //check if filtered data contains project info
      let project = this.filteredData.find(data => { return data.project.projectId == property.projectId });
      //create project if not provided
      if (!project) {
        this.filteredData.push({ "project": this.createNewProject(property), "valuationEvents": [] });
        project = this.filteredData.find(data => { return data.project.projectId == property.projectId })
        this.mappingValuationData(project, property, true);
      } else {
        this.mappingValuationData(project, property);
      }
    });
  }

  /**
  * Filter argus tasks data
  */
  filterArgusTasks() {
    this.argusTasks.forEach(property => {
      //check if filtered data contains project info
      let project = this.filteredData.find(data => { return data.project.projectId == property.projectId });
      //create project if not provided
      if (!project) {
        this.filteredData.push({ "project": this.createNewProject(property), "valuationEvents": [] });
        project = this.filteredData.find(data => { return data.project.projectId == property.projectId })
        this.mappingValuationData(project, property, true);
      } else {
        this.mappingValuationData(project, property);
      }
    });
  }

  /**
  * Filter valorem tasks data
  */
  filterValoremTasks() {
    this.valoremTasks.forEach(valoremTask => {
      let project = this.filteredData.find(data => data.project.projectId == valoremTask.projectId);
      if (project) {
        this.mappingValuationDataOfValoremTask(project, valoremTask);
      } else {
        this.filteredData.push({ "project": this.createNewProject(valoremTask), "valuationEvents": [] });
        project = this.filteredData.find(data => { return data.project.projectId == valoremTask.projectId })
        this.mappingValuationDataOfValoremTask(project, valoremTask, true);
      }
    });
  }

  /**
  * Filter general tasks data
  *  @param data Tasks data
  */
  filterGeneralTasks(data: any) {
    data?.generalTasks?.forEach(task => {
      //check if filtered data contains project info
      let project = this.filteredData.find(data => { return data.project.projectId == task.projectId });
      //create project if not provided
      if (!project && task.projectId) {
        this.filteredData.push({ "project": this.createNewProject(task), "valuationEvents": [] });
        project = this.filteredData.find(data => { return data.project.projectId == task.projectId });
        this.mappingGeneralValuationData(project, task, true);
      } else {
        this.mappingGeneralValuationData(project, task);
      }
    });
  }

  /**
   * Maps valorem task valuation data
   * @param project Project information
   * @param valoremTask Valorem task information
   * @param createNewValuationEvent Flag indicating weather it is needed to create a new valuation event
   */
  mappingValuationDataOfValoremTask(project: any, valoremTask: any, createNewValuationEvent = false) {
    let valEvent = project.valuationEvents.find(valEvent => valEvent.valEvent.oid == valoremTask.taskValuationPortfolioId);
    if (createNewValuationEvent || !valEvent)
      project.valuationEvents.push(this.createNewValEvent(valoremTask));
    valEvent = project.valuationEvents.find(valEvent => valEvent.valEvent.oid == valoremTask.taskValuationPortfolioId);
    valoremTask.properties.forEach(property => {
      //check if filtered data contains valuation task info
      let task = valEvent.tasks.find(data => { return data.taskInfo.oid == valoremTask.projectTaskId });
      //create task if not provided
      if (!task) {
        valEvent.tasks.push(this.createNewTask(valoremTask));
        task = valEvent.tasks.find(data => { return data.taskInfo.oid == valoremTask.projectTaskId })
        task.properties.push(property);
      } else {
        task.properties.push(property);
      }
    });

    project.valuationEvents.sort((a, b) => { return a.oid > b.oid ? -1 : 1 });
  }

  /**
   * Mapping valuation event in filtered data
   * @param property Property item
   * @param createNewValuationEvent Create new property true or false
   */
  mappingValuationData(project: any, property: any, createNewValuationEvent = false) {
    //check if filtered data contains valuation event info
    let valuationEvent = project.valuationEvents.find(data => { return data.valEvent.oid == property.portfolioId });
    //create valuation event if not provided
    if (createNewValuationEvent || !valuationEvent)
      project.valuationEvents.push(this.createNewValEvent(property));
    valuationEvent = project.valuationEvents.find(data => { return data.valEvent.oid == property.portfolioId });
    this.mappingTasksData(valuationEvent, property);

    project.valuationEvents.sort((a, b) => { return a.oid > b.oid ? -1 : 1 });
  }

  /**
   * Mapping valuation event in filtered data
   * @param property Property item
   * @param createNewValuationEvent Create new property true or false
   */
  mappingGeneralValuationData(project: any, property: any, createNewValuationEvent = false) {
    if (project) {
      //check if filtered data contains valuation event info
      let valuationEvent = project.valuationEvents.find(data => { return data.valEvent.oid == property.portfolioId });
      //create valuation event if not provided
      if (createNewValuationEvent || !valuationEvent)
        project.valuationEvents.push(this.createNewValEvent(property));
      valuationEvent = project.valuationEvents.find(data => { return data.valEvent.oid == property.portfolioId });
      this.mappingGeneralTasksData(valuationEvent, property);

      project.valuationEvents.sort((a, b) => { return a.oid > b.oid ? -1 : 1 });
    }
  }

  /**
   * Mapping task in filtered data
   * @param valuationEvent Valuation event
   * @param property Property
   */
  mappingTasksData(valuationEvent: { tasks: Array<any>, valEvent: any }, property: any) {
    //check if filtered data contains valuation task info
    let task = valuationEvent.tasks.find(data => { return data.taskInfo.oid == property.taskId });
    //create task if not provided
    if (!task) {
      valuationEvent.tasks.push(this.createNewTask(property));
      task = valuationEvent.tasks.find(data => { return data.taskInfo.oid == property.taskId })
      task.properties.push(property);
    } else {
      task.properties.push(property);
    }
  }

  /**
   * Mapping task in filtered data
   * @param valuationEvent Valuation event
   * @param property Property
   */
  mappingGeneralTasksData(valuationEvent: any, property: any) {
    //check if filtered data contains valuation task info
    let task = valuationEvent.tasks.find(data => { return data.taskInfo.oid == property.oid });
    //create task if not provided
    if (!task) {
      valuationEvent.tasks.push(this.createNewGeneralTask(property));
      task = valuationEvent.tasks.find(data => { return data.taskInfo.oid == property.oid })
      task.properties.push(property);
    } else {
      task.properties.push(property);
    }
  }

  /**
   * Crate new project
   * @param data User task
   */
  createNewProject(data: any): any {
    return {
      projectId: data.projectId,
      projectName: data.projectName,
      showContent: this.activeProjectId == data.projectId
    };
  }

  /**
   * Create new valuation event
   * @param data User task
   */
  createNewValEvent(data): any {
    return {
      valEvent: {
        oid: data.portfolioId ?? data.taskValuationPortfolioId,
        name: data.valuationName ?? data.portfolioName,
        date: data?.valuationDate,
        showContent: (this.activeValEventId == data.portfolioId && data.portfolioId != null) || ((this.activeValEventId == data.taskValuationPortfolioId && data.taskValuationPortfolioId != null))
      },
      tasks: []
    };
  }

  /**
   * Create new task
   * @param data User task
   */
  createNewTask(data): any {
    return {
      "taskInfo": {
        "taskName": data.taskName,
        "stepOrderNumber": data?.stepOrderNumber,
        "taskOrderNumber": data?.taskOrderNumber,
        "portfolioId": data.portfolioId ?? data.taskValuationPortfolioId,
        "oid": data.taskId ?? data.projectTaskId,
        "isCompleted": data?.taskActionStage == 4,
        "actionStage": data?.taskActionStage,
        "taskType": data.taskTypeId,
        "projectStepId": data.projectStepId ?? data.stepId
      },
      "properties": []
    }
  }

  /**
   * Create new task
   * @param data User task
   */
  createNewGeneralTask(data): any {
    return {
      "taskInfo": {
        "taskName": data.name,
        "taskOrderNumber": data.orderNumber,
        "portfolioId": data.portfolioId,
        "oid": data.oid,
        "isCompleted": false,
        "isGeneralTask": true,
        "projectRequestDate": data.projectRequestDate,
        "capForceProject": data.capForceProject,
        "approverFirstName": data.approverFirstName,
        "approverLastName": data.approverLastName,
        "approverId": data.approverId
      },
      "properties": []
    }
  }

  /**
   * Change approval matrix on task click
   * @param task Task
   */
  onTaskClick(task: any, valuationEvent: any, item: any) {
    this._raciNavigationService.onDisplayProjectDetails(false);
    this._raciNavigationService.onDisplayTaskApprovalHistoryDialog(false);
    this._raciNavigationService.onDisplayApprovalHistory(false);
    this._raciNavigationService.onHideApprovalHistoryIcon(true);
    this._raciNavigationService.isGeneralTaskActive(false);

    if (task?.taskInfo.taskType == TaskTypeEnum.ValoremFunction) {

      this._raciNavigationService.onDisplayApprovalHistory(false);
    }
    else {
      this.hideApprovalHistoryIcon();
      this._raciNavigationService.showTaskApprovalHistoryIcon(false);
    }

    this.setTaskAsActive(task, valuationEvent, item);
    this.setTaskContent(task);
    this.updateNavigationAfterActiveTaskChange(task);
    this._raciNavigationService.isApprovalClicked(false);
    this._raciNavigationService.showApprovalMatrixDialog(false);
  }

  /**
   * Sets active task content
   * @param task Active (selected) task
   */
  setTaskContent(task: any) {
    if (task?.taskInfo.taskType == TaskTypeEnum.ValoremFunction || task?.taskInfo.taskType == TaskTypeEnum.ArgusFunction || task?.taskInfo.taskType == TaskTypeEnum.ArgusSyncFunction) {
      this.approvalMatrixOfFunctionTask.emit(task?.properties);
    }
    else if (task?.taskInfo.taskType == TaskTypeEnum.ValuationEventFunction) {
      this.setValuationSetupTask.emit();
    }
    else {
      this.approvalMatrixOfTask.emit(this.getApprovalOfTaks(task.taskInfo.portfolioId, task.taskInfo.oid));
    }
  }

  /**
   * Updates navaigation after active task change
   * @param task Selected task
   */
  updateNavigationAfterActiveTaskChange(task: any) {
    this.resetNavigation();
    this.eventBus.broadcastEvent("setActivePortfolioId", task?.taskInfo.portfolioId);

    if (task?.taskInfo.taskType == TaskTypeEnum.Approval || task?.taskInfo.taskType == TaskTypeEnum.Undefined) {
      this._raciNavigationService.showTaskApprovalHistoryIcon(true);
      this.hideApprovalHistoryIcon();
    }

    this._raciNavigationService.showSetupValuationEventSaveIcon(task?.taskInfo.taskType == TaskTypeEnum.ValuationEventFunction);
    this._raciNavigationService.showSetupValuationEffectiveDatesSaveIcon(task?.taskInfo.taskType == TaskTypeEnum.ValuationEffectiveDatesSetup);
  }

  /**
   * Displays Valorem history icon
   */
  displayValoremHistoryIcon() {
    this._raciNavigationService.showValoremHistoryIcon(true);
    this._raciNavigationService.onHideApprovalHistoryIcon(true);
    this._raciNavigationService.showTaskApprovalHistoryIcon(false);
  }

  /**
   * Hides approval history icon
   */
  hideApprovalHistoryIcon() {
    this._raciNavigationService.showValoremHistoryIcon(false);
    this._raciNavigationService.onDisplayApprovalHistory(false);
    this._raciNavigationService.onHideApprovalHistoryIcon(true);
  }

  /**
   * Sets active task
   * @param task Selected task
   * @param valuationEvent Valuation event to which the task is linked to
   * @param item
   */
  setTaskAsActive(task: any, valuationEvent: any, item: any) {
    this.activeProjectId = item.project.projectId;
    this.activeValEventId = valuationEvent.valEvent.oid;
    this.activeTaskId = task.taskInfo.oid;
    this.activeTaskAssignment = false;
    this.onChangeUserActiveTask.emit(this.activeTaskId);
  }

  /**
   * Hide approval history dialog
   * @param task
   * @param item
   */
  closeApprovalHistoryDialog() {
    this._raciNavigationService.onDisplayTaskApprovalHistoryDialog(false);
    this.eventBus.broadcastEvent("onDisplayProjectDetails", false);
    this._raciNavigationService.onDisplayApprovalHistory(false);
  }

  /**
 * Change general task approval matrix on task click
 * @param task Task
 */
  onGeneralTaskClick(task: any, item: any = null) {
    this.activeGeneralTask = task;
    this._raciNavigationService.onDisplayTaskApprovalHistoryDialog(false);
    this._raciNavigationService.onDisplayProjectDetails(false);
    this._raciNavigationService.onDisplayApprovalHistory(false);
    this._raciNavigationService.onHideApprovalHistoryIcon(false);
    this.closeApprovalHistoryDialog();
    this.eventBus.broadcastEvent("hideApprovalHistoryIcon", false);
    this._raciNavigationService.showValoremHistoryIcon(false);
    this._raciNavigationService.showTaskApprovalHistoryIcon(false);
    this.activeTaskId = task.oid;
    this.onChangeUserActiveTask.emit(this.activeTaskId);
    this._raciNavigationService.isGeneralTaskActive(true);
    this.activeTaskAssignment = true;
    this.activeValEventId = task?.taskValuationPortfolioId;
    if (this.activeValEventId) {
      this.activeProjectId = item.project.projectId;
      this.activeTaskAssignment = false;
    } else {
      this.activeProjectId = null;
    }

    this.generalTaskApprovalMatrix.emit(task);
    this.resetNavigation();
    this._raciNavigationService.showSyncWithArgus(false);
    this._raciNavigationService.isApprovalClicked(false);
    this._raciNavigationService.showApprovalMatrixDialog(false);
  }

  /**
   * Resets navigation actions
   */
  private resetNavigation() {
    this._raciNavigationService.onSelectRow(false);
    this._raciNavigationService.onShowPreviewPortfolioIcon(true);
    this._raciNavigationService.onShowProjectDetailsIcon(false);
    this._raciNavigationService.onShowApproveIcon(false);
    this._raciNavigationService.showSyncWithArgus(false);
  }

  /**
   * Retrive approval tasks
   * @param portfolioId Portfolio Id
   * @param taskId Task Id
   */
  getApprovalOfTaks(portfolioId: any, taskId: any): any {
    return this.approvalTasks.filter(task => { return task.portfolioId == portfolioId && task.taskId == taskId });
  }

  /**
   *  Toggles the item with the specified valEvent
   * @param item
   * @param valEvent
   */
  toggle(item: any, valEvent: any) {
    item.showContent = !item.showContent;
    if (valEvent)
      this._raciNavigationService.setActivePortfolio(item)
  }

  /**
   * Update completed status of tasks
   * @param user Current user
   */
  updateTasksStatus(user: any) {
    this.filteredData?.forEach(project => {
      project.valuationEvents.forEach(valEvent => {
        valEvent?.tasks?.forEach(task => {

          if (task?.taskInfo.taskType != TaskTypeEnum.Approval && task?.taskInfo.taskType != TaskTypeEnum.Undefined) {
            return;
          }

          if (!task.taskInfo.isGeneralTask) {
            task.taskInfo.actionStage = this.userTasks?.approvalTasks?.find(userTask => userTask.taskId == task.taskInfo.oid)?.taskActionStage ?? this.userTasks.valoremTasks.find(userTask => userTask.projectTaskId == task.taskInfo.oid)?.taskActionStage ?? this.userTasks.argusTasks.find(userTask => userTask.taskId == task.taskInfo.oid)?.taskActionStage ?? this.userTasks.valuationEventTasks?.find(userTask => userTask.taskId == task.taskInfo.oid)?.taskActionStage;
            task.taskInfo.isCompleted = task.taskInfo.actionStage == ActionStageEnum.Completed;
          }

          if (task.taskInfo.actionStage != ActionStageEnum.Completed) {
            const properties = task?.properties?.find(property => { return this.hasAllApprovalsFromCurrentUser(property, user); });
            task.taskInfo.isCompleted = !task?.taskInfo?.isGeneralTask && properties == null;
            this.shouldRefreshUserTasks = !!task.properties.find(item => { return item.dateApproved1 && item.dateApproved2 && item.dateApproved3 });
          }
        })
      })
    })
  }

  /**
   * Opens confirm dialog for task completion
   * @param task
   */
  openDialogForCompletingFunctionTask(task: any) {
    if (task.taskInfo.taskType !== TaskTypeEnum.Approval && task.taskInfo.taskType !== TaskTypeEnum.Undefined) // || task.taskInfo.taskType === TaskTypeEnum.ArgusFunction || task.taskInfo.taskType === TaskTypeEnum.ValuationEventFunction || task.taskInfo.taskType === TaskTypeEnum.ValuationEffectiveDatesSetup) {
    {
      this.eventBus.broadcastEvent("openConfirmDialog", {
        success: () => {
          this.completeFunctionTask(task);
        },
        cancel: () => {
          //Nothing
        },
      }, 'completeRaciTask');
    }
  }

  /**
   * Checks if property has all approvals from current user
   * @param property Specified property ino
   * @param user User info
   */
  private hasAllApprovalsFromCurrentUser(property: any, user: any) {
    return (property.approval1 == user.OID && !property.dateApproved1) ||
      (property.approval2 == user.OID && !property.dateApproved2) ||
      (property.approval3 == user.OID && !property.dateApproved3)
  }

  /**
   * Complete task
   * @param item Task
   */
  completeFunctionTask(item: any) {
    this.updateTaskCompletionStatus(item);
    this._subscriptions.push(this._raciService.completeTask(item.taskInfo).subscribe(data => {
      if (data) {
        this.filteredData?.forEach(project => {
          project.valuationEvents.forEach(valEvent => {
            const task = valEvent?.tasks?.filter(t => t.taskInfo.oid == item.taskInfo.oid)[0];
            this.refreshAfterFunctionTaskIsCompleted(item?.taskInfo?.projectStepId, item?.taskInfo?.portfolioId, task);
          });
        })
      }
    }));
  }

  /**
   * Refresh function task after completion
   * @param projectStepId Project step id
   * @param projectStepId Portfolio id
   * @param task Task information
   */
  refreshAfterFunctionTaskIsCompleted(projectStepId: number, portfolioId: number, task: any) {
    if (task.taskInfo.taskType !== TaskTypeEnum.Approval && task.taskInfo.taskType !== TaskTypeEnum.Undefined) {
      this.onCompleteTask.emit(task.taskInfo);
      task.taskInfo.isCompleted = true;
      this.disableFunctionTaskActions();
      this._raciService.completeStep({ projectStepId: projectStepId, stepValuationPortfolioId: portfolioId }).subscribe();
    }
  }

  /**
   * Updates the completion status of a task item
   * @param item The item to update
   */
  private updateTaskCompletionStatus(item: any) {
    // Set task properties for completion
    item.taskInfo.projectTaskId = item.taskInfo.oid;
    item.taskInfo.isCompleted = true;
    item.taskInfo.taskDateModified = new Date();
    item.taskInfo.taskModifiedBy = this.currentUser?.OID;
    item.taskInfo.taskModifiedByUserName = this.currentUser?.User_Description;
    item.taskInfo.taskActionStage = ActionStageEnum.Completed;
  }

  /**
   * Disables function tasks navigation actions
   */
  private disableFunctionTaskActions() {
    // Disable function task actions
    this._raciNavigationService.onSyncPortfolioWithArgus(false, 0);
    this._raciNavigationService.showSyncWithArgus(false);
    this._raciNavigationService.onSyncWithValorem(false);
  }
}
